/**
 * Button Response Handlers
 * Pure functions for handling button responses in previous step edits
 */

import type { Dispatch } from '@reduxjs/toolkit';

import type { RootState } from '../data-layer/reduxTypes';
import { addMessage } from '../data-store/copilotSlice';
import { STEP_NAMES } from '../services';

import {
  createAccountHelperMessage,
  createConnectionHelperMessage,
  createContinueSuccessMessage,
} from './buttonResponseMessageFactory';
import { buildConnectionPayload } from './connectionUtils';
import savePreviousStepConfiguration from './previousStepApiUtils';
import { isAccountStep } from './stepMappingUtils';

/**
 * Handle continue with existing instructions
 */
export const handleContinueWithInstructions = async (
  dispatch: Dispatch,
  getState: () => unknown,
  services: {
    testConnection?: (payload: unknown) => Promise<unknown>;
    saveUseCaseConfiguration?: (payload: unknown) => Promise<unknown>;
  }
): Promise<void> => {
  const state = getState() as {
    copilot?: {
      navigation?: { activeStep?: string };
      stepFormData?: Record<string, Record<string, unknown>>;
      parsedTemplates?: { Connection?: { instructions?: string } };
      ui?: { endpointKey?: string };
      connectionPrompt?: string;
      endpointKey?: string;
    };
  };

  const activeStep = state.copilot?.navigation?.activeStep ?? '';
  const formData = state.copilot?.stepFormData?.[activeStep] ?? {};

  // Add user "Yes" response first
  dispatch(
    addMessage({
      id: `yes-response-${Date.now()}`,
      type: 'user_response',
      subType: 'userResponseYes',
      text: 'Yes',
      timestamp: Date.now(),
    })
  );

  try {
    // Special handling for Connection step with existing prompt
    if (activeStep === STEP_NAMES.CONNECTION) {
      const existingPrompt =
        state.copilot?.connectionPrompt ||
        state.copilot?.parsedTemplates?.Connection?.instructions ||
        '';
      const endpointKey =
        state.copilot?.endpointKey || state.copilot?.ui?.endpointKey;
      const applicationtype = String(
        state.copilot?.stepFormData?.General?.['securitysystems.systemtype'] ||
          'CUA'
      );

      const connectionPayload = buildConnectionPayload(
        formData,
        existingPrompt
      );

      await services.saveUseCaseConfiguration?.({
        applicationtype: String(applicationtype),
        endpointKey: Number(endpointKey),
        body: connectionPayload,
      });

      const generalData = state.copilot?.stepFormData?.General || {};

      await services.testConnection?.({
        endpointName: String(generalData['securitysystems.systemname'] || ''),
      });
    } else {
      await savePreviousStepConfiguration(
        activeStep,
        formData,
        services,
        getState as () => RootState
      );
    }

    dispatch(addMessage(createContinueSuccessMessage()));
  } catch {
    // Handle error silently
  }
};

/**
 * Handle modify instructions
 */
export const handleModifyInstructions = (
  dispatch: Dispatch,
  getState: () => unknown
): void => {
  const state = getState() as {
    copilot?: { navigation?: { activeStep?: string } };
  };

  const activeStep = state.copilot?.navigation?.activeStep ?? '';

  // Add user "No" response first
  dispatch(
    addMessage({
      id: `no-response-${Date.now()}`,
      type: 'user_response',
      subType: 'userResponseNo',
      text: 'No',
      timestamp: Date.now(),
    })
  );

  // Then add helper message after "No" response
  if (activeStep === STEP_NAMES.CONNECTION) {
    dispatch(addMessage(createConnectionHelperMessage()));
  } else if (isAccountStep(activeStep)) {
    dispatch(addMessage(createAccountHelperMessage()));
  }
};
